import React, { useEffect, useRef, useState } from "react";
import { AppSettingContext } from '../components/Application';
import CheckMyBalanceStep from '../components/giftCard/CheckMyBalanceStep';
import GiftCardDetails from '../components/giftCard/GiftCardDetails';
import RegiftCardCustomizeStep from '../components/giftCard/RegiftCardCustomizeStep';
import RegiftCardPaymentStep from '../components/giftCard/RegiftCardPaymentStep';
import RegiftCardRecipientsStep from '../components/giftCard/RegiftCardRecipientsStep';
import RegiftCardStep from '../components/giftCard/RegiftCardStep';
import DebugRegiftDataPreivewComponent from '../components/giftCard/debug-mode/DebugRegiftDataPreivewComponent';
import ErrorComponent from '../components/shared/ErrorComponent';
import LoadingComponent from '../components/shared/LoadingComponent';
import * as GlobalConstants from "../constants/global";
import { fixPreviewDetails, resetRecipientsCountOnRecipientStep } from '../helpers/GiftCardHelper';
import { ICheckBalanceStep } from '../models/CheckBalanceStep.model';
import { ICustomizeStep } from '../models/CustomizeStep.model';
import { IRecipientsStep } from '../models/RecipientsStep.model';
import { IRegiftCard, RegiftCardModel } from '../models/RegiftCard.model';
import { IRegiftCardStep } from '../models/RegiftCardStep.model';
import { RegiftCardStateEnum } from '../models/enums/RegiftCardStateEnum';
import DataService from '../services/DataService.service';
import './RegiftCardPage.scss';


// Global context of Buy Gift card step
export const RegiftCardContext = React.createContext({} as RegiftCardModel);

const RegiftCardPage = () => {
    const ref = useRef(true);
    const { debugMode, labels, cardSettings } = React.useContext(AppSettingContext);

    const [culture, setCulture] = useState('en-GB');
    const [pageModel, setPageModel] = useState({} as IRegiftCard);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasRequestToSaveBasket, setRequestHasToSaveBasket] = useState(false);
    const [error, setError] = useState(null);

    const [previewTemplateUrl, setPreviewTemplateUrl] = useState("");
    const [previewMediaUrl, setPreviewMediaUrl] = useState("");
    const [previewPersonalMessage, setPreviewPersonalMessage] = useState("");
    const [hasToShowPreviewBox, setHasToShowPreviewBox] = useState(false);
    const [previewRegiftData, setPreviewRegiftData] = useState<ICheckBalanceStep>();

    const [firstTemplateUrl, setFirstTemplateUrl] = useState("");
    const defaultPersonalMessageText = labels.get("GiftCard.App.Steps.Customise.PersonalMessage.DefaultText");

    // INIT
    useEffect(() => {
        const firstRender = ref.current;
        if (firstRender) {
            ref.current = false;
            //console.debug("INIT (RegiftCardPage)");
            setIsLoading(true);
            setCulture(document.documentElement.lang);

            // Get basket-id from local storage
            let baskedId = localStorage.getItem(GlobalConstants.LOCALSTORAGE_KEY_BASKET_ID);
            if (!baskedId) baskedId = "";
            //
            DataService.getRegiftCardBasket(culture, baskedId)
                .then(response => {
                    if (response && response.id && response.id.trim()) {
                        //Set basket-id back to local storage
                        localStorage.setItem(GlobalConstants.LOCALSTORAGE_KEY_BASKET_ID, response.id);

                        const model = RegiftCardModel.MapModel(response);

                        if (model && model.customizeStep) {
                            if (model.customizeStep.giftCardUrl && model.customizeStep.giftCardUrl.trim()) {
                                setFirstTemplateUrl(model.customizeStep.giftCardUrl);
                            }

                            if (model.customizeStep.personalMessage && model.customizeStep.personalMessage.trim()) {
                                setPreviewPersonalMessage(model.customizeStep.personalMessage);
                            } else {
                                model.customizeStep.personalMessage = "";
                            }

                            if (model.customizeStep.mediaUrl && model.customizeStep.mediaUrl.trim()) {
                                setPreviewMediaUrl(model.customizeStep.mediaUrl);
                            }
                        }

                        setPageModel(model);
                    } else {
                        setError("Api response is an invalid gift card model")
                    }
                }).catch(error => {
                    console.error(error);
                });

            // Initialize state to store custom uploaded Templates and Medias
            // Load all templates; Then use first one as default selection when use save first step
            DataService.getGiftCardsTemplates(culture)
                .then(response => {
                    if (response) {
                        if (response && response.length > 0) {
                            setFirstTemplateUrl(response[0].imageUrl);
                        }
                    }
                }).catch(error => {
                    console.error(error);
                });
        }
    }, []);
    // *****

    useEffect(() => {
        if (pageModel.id && pageModel.id.trim()) {
            setIsLoading(false);
            setTimeout(() => {
                setIsSaving(false);
            }, 500);
        }
    }, [pageModel]);

    const handleUpdateState = (newState: RegiftCardStateEnum): void => {
        const pageModelCopy = Object.assign({}, pageModel);
        pageModelCopy.currentState = newState;
        setPageModel(pageModelCopy);
    };

    const handleModelState = (isDirty: boolean, step: RegiftCardStateEnum): void => {
        const pageModelCopy = Object.assign({}, pageModel);

        switch (step) {
            case RegiftCardStateEnum.CHECK_BALANCE:
                pageModelCopy.checkBalanceStep.isDirty = isDirty;
                break;
            case RegiftCardStateEnum.REGIFT_CARD:
                pageModelCopy.regiftCardStep.isDirty = isDirty;
                break;
            case RegiftCardStateEnum.ENTER_RECEPIENTS:
                pageModelCopy.recipientsStep.isDirty = isDirty;
                break;
            case RegiftCardStateEnum.CUSTOMIZE_CARD:
                pageModelCopy.customizeStep.isDirty = isDirty;
                break;
            case RegiftCardStateEnum.REVIEW_PAY:
                pageModelCopy.paymentStep.isDirty = isDirty;
                break;
            default: break;
        }

        setPageModel(pageModelCopy);
    };

    // ***
    // Check Balance Step
    const handleCheckBalanceSubmit = (model: ICheckBalanceStep): void => {

        if (model) {
            setIsSaving(true);
            const pageModelCopy = Object.assign({}, pageModel);

            pageModelCopy.checkBalanceStep.isDirty = false;
            pageModelCopy.checkBalanceStep.redeemCode = model.redeemCode;
            pageModelCopy.checkBalanceStep.giftCardCode = model.giftCardCode;
            pageModelCopy.checkBalanceStep.remainingBalance = model.remainingBalance;
            pageModelCopy.checkBalanceStep.currencySymbol = model.currencySymbol;

            // Update page state
            pageModelCopy.currentState = RegiftCardStateEnum.REGIFT_CARD;
            setPageModel(pageModelCopy);

            setPreviewRegiftData(null);
        }
    };

    // ***
    // Regift card Step
    const handleRegiftCardSubmit = (model: IRegiftCardStep): void => {
        if (model) {
            setIsSaving(true);

            const pageModelCopy = Object.assign({}, pageModel);

            pageModelCopy.regiftCardStep.isDirty = false;
            pageModelCopy.regiftCardStep.value = model.value;

            // Update Recipients
            //------------------------
            const responseModel = resetRecipientsCountOnRecipientStep(1, pageModelCopy.recipientsStep);
            pageModelCopy.recipientsStep.sendIndividually = responseModel.sendIndividually;
            pageModelCopy.recipientsStep.recipients = (responseModel as IRecipientsStep).recipients;

            // Customize
            if (!previewTemplateUrl.trim()) {
                setPreviewTemplateUrl(firstTemplateUrl);
                pageModelCopy.customizeStep.giftCardUrl = firstTemplateUrl;
                fixPreviewDetails();
                setRequestHasToSaveBasket(true);
            }

            // Update page state
            pageModelCopy.currentState = RegiftCardStateEnum.ENTER_RECEPIENTS;
            setPageModel(pageModelCopy);
        }
    }

    // ***
    // Recipients Step
    const handleRecipientsSubmit = (model: IRecipientsStep): void => {
        setIsSaving(true); //to refresh components

        //console.debug("handleRecipientsStepSubmit", model);

        const pageModelCopy = Object.assign({}, pageModel);
        pageModelCopy.recipientsStep.isDirty = false;

        pageModelCopy.recipientsStep.sendBy = model.sendBy;
        pageModelCopy.recipientsStep.senderSameAsRecipient = model.senderSameAsRecipient;
        pageModelCopy.recipientsStep.sendIndividually = model.sendIndividually;
        pageModelCopy.recipientsStep.sendItLater = model.sendItLater;

        pageModelCopy.recipientsStep.sender = Object.assign({}, model.sender);
        pageModelCopy.recipientsStep.scheduleSendDetails = Object.assign({}, model.scheduleSendDetails);

        pageModelCopy.recipientsStep.recipients = [];
        for (let i = 0; i < model.recipients.length; i++) {
            pageModelCopy.recipientsStep.recipients.push({
                firstName: model.recipients[i].firstName,
                firstNameIsValid: model.recipients[i].firstNameIsValid,
                lastName: model.recipients[i].lastName,
                lastNameIsValid: model.recipients[i].lastNameIsValid,
                email: model.recipients[i].email,
                emailIsValid: model.recipients[i].emailIsValid,
                phone: model.recipients[i].phone,
            });
        }

        pageModelCopy.currentState = RegiftCardStateEnum.CUSTOMIZE_CARD;
        setPageModel(pageModelCopy);
    }
    // ***
    // Customize Card Step
    const handleTemplateChange = (templateUrl: string): void => {

        setPreviewTemplateUrl(templateUrl);
        //Do not update pageModelCopy - do that on Save
        //just create a temp state variable and pass it to details component
        //same like gif
    }

    const handleMediaChange = (mediaUrl: string): void => {
        setPreviewMediaUrl(mediaUrl);
    }

    const handlePreview = (draftPersonalMessage: string | null): void => {

        setPreviewPersonalMessage("");//clear draft message, so the preview box should show ICustomizeStep based one
        if (draftPersonalMessage && draftPersonalMessage.trim()) {
            //set draft message only if passes
            setPreviewPersonalMessage(draftPersonalMessage);
        }

        setHasToShowPreviewBox(true);
    };

    const handleCustomizeSubmit = (model: ICustomizeStep): void => {
        if (model) {
            setIsSaving(true); //to refresh components

            // Update Page Model
            const pageModelCopy = Object.assign({}, pageModel);
            pageModelCopy.customizeStep.isDirty = false;
            pageModelCopy.customizeStep.giftCardUrl = model.giftCardUrl;
            pageModelCopy.customizeStep.mediaUrl = model.mediaUrl;
            pageModelCopy.customizeStep.personalMessage = model.personalMessage;

            setPreviewPersonalMessage(model.personalMessage);

            //Update BASKET CMS
            saveBasket(pageModelCopy);
        }
    }

    const saveBasket = (model: IRegiftCard): void => {
        if (!model) return;

        //Do not save default message
        if (model.customizeStep.personalMessage === defaultPersonalMessageText) {
            model.customizeStep.personalMessage = '';
        }

        DataService.updateRegiftCardBasket(culture, model.id, model)
            .then(response => {
                //console.debug(response);
                if (response && response.id && response.id.trim()) {

                    //console.debug("updateBuyGiftCardBasket", response);
                    //debugger;

                    setRequestHasToSaveBasket(false); //basket was updated
                    setHasToShowPreviewBox(true);

                    const model = RegiftCardModel.MapModel(response);
                    model.currentState = RegiftCardStateEnum.REVIEW_PAY;
                    setPageModel(model);

                } else {
                    setError("Unable to update basket")
                }
            })
            .catch(console.error)
            .finally(() => {
                //
            });
    }

    return (
        <>
            {error ? (
                <ErrorComponent />
            ) : (
                <RegiftCardContext.Provider value={pageModel}>
                    {isLoading &&
                        <LoadingComponent large={true} />
                    }
                    {(!isLoading) &&
                        <div className="container">
                            <div className="flex-container">
                                <div className="forms">
                                    <CheckMyBalanceStep
                                        handleDirtyState={(isDirty) => handleModelState(isDirty, RegiftCardStateEnum.CHECK_BALANCE)}
                                        handleBeginEdit={() => handleUpdateState(RegiftCardStateEnum.CHECK_BALANCE)}
                                        handleCheckBalance={setPreviewRegiftData}
                                        handleSubmit={handleCheckBalanceSubmit}
                                    />

                                    <RegiftCardStep
                                        isSaving={isSaving}
                                        handleDirtyState={(isDirty) => handleModelState(isDirty, RegiftCardStateEnum.REGIFT_CARD)}
                                        handleBeginEdit={() => handleUpdateState(RegiftCardStateEnum.REGIFT_CARD)}
                                        handleSubmit={(model) => handleRegiftCardSubmit(model)} />

                                    <RegiftCardRecipientsStep
                                        isSaving={isSaving}
                                        handleDirtyState={(isDirty) => handleModelState(isDirty, RegiftCardStateEnum.ENTER_RECEPIENTS)}
                                        handleBeginEdit={() => handleUpdateState(RegiftCardStateEnum.ENTER_RECEPIENTS)}
                                        handleSubmit={(model) => handleRecipientsSubmit(model)} />

                                    <RegiftCardCustomizeStep
                                        isSaving={isSaving}

                                        hasRequestToSaveBasket={hasRequestToSaveBasket}
                                        handleBeginEdit={() => handleUpdateState(RegiftCardStateEnum.CUSTOMIZE_CARD)}
                                        handleDirtyState={(isDirty) => handleModelState(isDirty, RegiftCardStateEnum.CUSTOMIZE_CARD)}
                                        handleTemplateChange={handleTemplateChange}
                                        handleMediaChange={handleMediaChange}
                                        handleSubmit={(model) => handleCustomizeSubmit(model)}
                                        handlePreview={handlePreview} />

                                    <RegiftCardPaymentStep
                                        isSaving={isSaving}

                                        giftCard={pageModel}
                                        model={pageModel.paymentStep}
                                        state={pageModel.currentState}

                                        handleDirtyState={(isDirty) => handleModelState(isDirty, RegiftCardStateEnum.REVIEW_PAY)}
                                        handleBeginEdit={() => handleUpdateState(RegiftCardStateEnum.REVIEW_PAY)}
                                        //handleSubmit={handlePaymentSubmit}
                                        handlePreview={() => handlePreview(null)}
                                        handleError={(message) => setError(message)}
                                    />

                                </div>

                                <div className="sidebar">
                                    <GiftCardDetails
                                        isSaving={isSaving}
                                        subpage={null}
                                        cardValue={pageModel.regiftCardStep.value}

                                        giftCardUrl={pageModel.customizeStep.giftCardUrl ?? ""}
                                        initialTemplateUrl={firstTemplateUrl}
                                        previewTemplateUrl={previewTemplateUrl}
                                        previewMediaUrl={previewMediaUrl}
                                        previewPersonalMessage={previewPersonalMessage}

                                        regiftData={previewRegiftData}
                                    />

                                    {debugMode && <DebugRegiftDataPreivewComponent isLoading={isLoading} isSaving={isSaving} />}
                                </div>
                            </div>

                            {/*<InstructionsAboutRegiftCard />*/}
                        </div>
                    }
                </RegiftCardContext.Provider>
            )}
        </>
    );
};
export default RegiftCardPage;
